/**
 * Homepage — V26 (approved prototype in Love_and_Photos_Dev_Handoff_V26, 20 Sep 2026).
 *
 * Sections in the approved order: hero, trust row, pricing (compact card that opens "Choose Your Coverage"),
 * how it works, our work, a team that gets it (looping muted video), verified reviews, closing band. The header,
 * footer and Grace are the site's shared components and are not rendered here. Prototype-only simulation
 * (fake login, fake booking page, fake reviews, invented photo-count variants) is not reproduced: prices come
 * from the one ladder, reviews from public.reviews, booking from the existing express checkout.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { SweepstakesProvider } from '@contexts/SweepstakesContext'
import SweepstakesModal from '@components/sweepstakes/SweepstakesModal'
import CoverageBuilder from '@components/homepage/CoverageBuilder'
import ReviewStream from '@components/homepage/ReviewStream'
import { HOMEPAGE_ASSETS } from '@/lib/homepage/assets'

/** Fired by the header's Book control so a click on the homepage opens pricing even when the hash is unchanged. */
export const OPEN_PRICING_EVENT = 'lp:open-pricing'
const PRICING_HASHES = new Set(['#pricing', '#negotiation-widget', '#pricingBuilder'])

const STEPS = [
  ['Book Your Package', 'Choose photo, video, or both and secure your date.'],
  ['We Match Your Photographer', 'We assign vetted talent based on location, style, and availability.'],
  ['Plan With Peace of Mind', 'We gather your timeline, details, and preferences.'],
  ['Meet Your Photographer', 'Connect before the wedding to align on final details.'],
  ['Wedding Day Coverage', 'Your team captures the moments that matter.'],
  ['Delivery & Download', 'Receive, download, share, and relive your finished gallery.']
]

function scrollToId(id, block = 'start') {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block })
}

function TeamVideo() {
  const [src, setSrc] = useState(HOMEPAGE_ASSETS.teamVideo.mp4_720)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(min-width: 900px)').matches) {
      setSrc(HOMEPAGE_ASSETS.teamVideo.mp4_1080)
    }
  }, [])
  return (
    <video
      className="team-video"
      autoPlay muted loop playsInline preload="metadata"
      poster={HOMEPAGE_ASSETS.teamVideo.poster}
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src={src} type="video/mp4" />
    </video>
  )
}

const Home = () => {
  const location = useLocation()
  const [pricingOpen, setPricingOpen] = useState(false)

  // Capture referral code from URL and persist for checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (code) {
      localStorage.setItem('pending_referral_code', code.toUpperCase().trim())
    }
  }, [])

  const openPricing = useCallback(() => {
    setPricingOpen(true)
    scrollToId('pricing', 'start')
  }, [])

  // Deep links: /#pricing (header Book, Grace's chip, old #negotiation-widget links) open the panel; other hashes scroll.
  useEffect(() => {
    const hash = location.hash
    if (!hash) return
    const t = setTimeout(() => {
      if (PRICING_HASHES.has(hash)) openPricing()
      else scrollToId(hash.slice(1))
    }, 60)
    return () => clearTimeout(t)
  }, [location.hash, openPricing])

  useEffect(() => {
    window.addEventListener(OPEN_PRICING_EVENT, openPricing)
    return () => window.removeEventListener(OPEN_PRICING_EVENT, openPricing)
  }, [openPricing])

  return (
    <SweepstakesProvider>
      <main className="lp-home" id="top">
        <section className="hero" aria-labelledby="hero-heading">
          <div className="hero-copy">
            <div>
              <div className="eyebrow">Your story, beautifully told.</div>
              <h1 id="hero-heading" className="serif">Wedding Photo<br />&amp; Video for<br />a Day Worth Remembering</h1>
              <p className="lede">The day goes fast. The memories shouldn’t.</p>
              <div className="hero-actions">
                <button type="button" className="btn rose" onClick={openPricing}>VIEW PRICING AND BOOK</button>
              </div>
            </div>
          </div>
          <div className="hero-photo" role="img" aria-label="Couple on their wedding day" style={{ backgroundImage: `url(${HOMEPAGE_ASSETS.hero})` }} />
        </section>

        <section className="shell" aria-label="Why couples choose Love & Photos">
          <div className="trust">
            <div className="trust-card"><div className="icon" aria-hidden="true">✓</div><h3>Vetted Talent</h3><p>All photographers are carefully screened and verified for quality.</p></div>
            <div className="trust-card"><div className="icon" aria-hidden="true">♡</div><h3>A Seamless Experience</h3><p>From booking to delivery, we make it easy.</p></div>
            <div className="trust-card"><div className="icon" aria-hidden="true">☆</div><h3>Beautiful, Consistent Quality</h3><p>A curated team and in-house editing.</p></div>
            <div className="trust-card"><div className="icon" aria-hidden="true">↔</div><h3>Flexible Plans</h3><p>Choose from packages or custom pricing to fit your needs.</p></div>
          </div>
        </section>

        <CoverageBuilder open={pricingOpen} onOpen={openPricing} onClose={() => setPricingOpen(false)} />

        <section className="approach" id="how" aria-labelledby="how-heading">
          <div className="approach-photo" role="img" aria-label="Bride getting ready" style={{ backgroundImage: `url(${HOMEPAGE_ASSETS.approach})` }} />
          <div className="approach-main">
            <div>
              <div className="eyebrow">How it works</div>
              <h2 id="how-heading" className="serif">Effortless from<br />“Yes” to “I Do”</h2>
              <p className="bodycopy">A simple path from choosing your coverage to receiving your finished gallery.</p>
            </div>
            <div className="journey-steps">
              {STEPS.map(([title, body], i) => (
                <div className="step" key={title}>
                  <div className="num">{String(i + 1).padStart(2, '0')}</div>
                  <p><b>{title}</b><br /><span>{body}</span></p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="work" id="work" aria-labelledby="work-heading">
          <div className="shell work-grid">
            <div>
              <div className="eyebrow">Our work</div>
              <h2 id="work-heading" className="serif">Real Weddings.<br />Real Moments.</h2>
              <p className="bodycopy">From quiet looks to unforgettable dance floors, see how we capture it all.</p>
              <Link to="/gallery" reloadDocument className="btn rose">Explore Galleries →</Link>
            </div>
            <div className="gallery" id="gallery">
              {HOMEPAGE_ASSETS.gallery.map(img => <img key={img.src} src={img.src} alt={img.alt} loading="lazy" />)}
            </div>
          </div>
        </section>

        <section className="team" id="team" aria-labelledby="team-heading">
          <TeamVideo />
          <div className="team-inner">
            <div className="team-copy">
              <div className="eyebrow">More than a vendor</div>
              <h2 id="team-heading" className="serif">A Team That<br />Gets It</h2>
              <p>We’re a passionate group of creatives, dance-floor pros, and problem-solvers who genuinely care about your experience — and your story.</p>
            </div>
            <div className="team-points">
              <div className="team-point"><div aria-hidden="true">♙</div><b>Vetted &amp; experienced talent</b></div>
              <div className="team-point"><div className="team-heart" aria-hidden="true">♡</div><b>A personalized matching process</b></div>
              <div className="team-point"><div aria-hidden="true">☆</div><b>In-house editing for a consistent, elevated style</b></div>
            </div>
          </div>
        </section>

        <ReviewStream />

        <section className="cta" aria-label="Book your date">
          <div className="shell cta-row">
            <div className="script">Let’s make<br />it official.</div>
            <div>
              <p>Ready when you are. Choose your coverage and secure your date.</p>
              <button type="button" className="btn light" onClick={openPricing}>Book My Date →</button>
            </div>
          </div>
        </section>
      </main>
      <SweepstakesModal />
    </SweepstakesProvider>
  )
}

export default Home
